/**
 * 纯手写锚定菜单（LeapWorldUI-02；D29 登记：未引定位原语，评估见工单完工报告）。
 * 触发钮 + 绝对定位面板；ESC / 外点关闭，ESC 与选中菜单项均回焦触发钮；↑/↓ 在可用菜单项间循环移动；
 * 动画不做（瞬时显隐，符合「动画一律 CSS」且无过渡需求）。面板不带翻转/碰撞检测——
 * 触发点都在视口内的高频场景（行操作、用户区），溢出由调用方用 align 调整。
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

export type DropdownMenuItem = {
  key: string
  label: ReactNode
  icon?: ReactNode
  /** 危险操作配色（删除、吊销一类） */
  danger?: boolean
  disabled?: boolean
  onSelect?: () => void
}

export type DropdownMenuProps = {
  /** 触发钮内容（图标或文案），渲染在内建按钮里 */
  trigger: ReactNode
  /** 触发钮可达名称（图标钮必填，中文） */
  triggerLabel: string
  items: DropdownMenuItem[]
  align?: 'start' | 'end'
  className?: string
  triggerClassName?: string
}

const itemSelector = 'button[role="menuitem"]:not(:disabled)'

export function DropdownMenu({ trigger, triggerLabel, items, align = 'end', className = '', triggerClassName = '' }: DropdownMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  const close = () => setOpen(false)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close()
    }
    document.addEventListener('mousedown', onPointerDown)
    // 打开后焦点进第一项，键盘用户立即可操作
    panelRef.current?.querySelector<HTMLElement>(itemSelector)?.focus()
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      // 冒泡阶段截住：use-overlay 的 ESC 监听挂在 document，菜单打开在 Modal/Drawer 内时
      // 一次 ESC 只关菜单，不带动外层浮层
      event.stopPropagation()
      close()
      triggerRef.current?.focus()
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const menuItems = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(itemSelector) ?? [])
    if (!menuItems.length) return
    const index = menuItems.indexOf(document.activeElement as HTMLElement)
    const next = event.key === 'ArrowDown' ? (index + 1) % menuItems.length : (index - 1 + menuItems.length) % menuItems.length
    menuItems[next]?.focus()
  }

  return (
    <div ref={rootRef} className={`relative inline-flex ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel}
        className={`inline-flex min-h-[var(--control-height)] min-w-[var(--control-height)] cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap shrink-0 rounded-[var(--radius-md)] px-2 text-sm text-[var(--color-muted)] transition-colors hover:bg-[var(--color-soft)] hover:text-[var(--color-ink)] focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)] max-md:min-h-11 max-md:min-w-11 ${triggerClassName}`.trim()}
        onClick={() => setOpen((current) => !current)}
      >
        {trigger}
      </button>
      {open ? (
        // z-50：锚定浮层层（分层约定见 use-overlay.ts）
        <div
          ref={panelRef}
          id={menuId}
          role="menu"
          aria-label={triggerLabel}
          className={`absolute top-full z-50 mt-1 min-w-44 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel)] p-1 shadow-[var(--shadow-md)] ${align === 'end' ? 'right-0' : 'left-0'}`}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={`flex w-full cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2.5 py-2 text-left text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11 ${
                item.danger
                  ? 'text-[var(--color-danger)] hover:bg-[var(--color-danger-soft)] focus-visible:bg-[var(--color-danger-soft)]'
                  : 'text-[var(--color-ink)] hover:bg-[var(--color-soft)] focus-visible:bg-[var(--color-soft)]'
              }`}
              onClick={() => {
                close()
                // 选中后面板卸载，焦点必须回到触发钮，否则键盘用户丢焦点
                triggerRef.current?.focus()
                item.onSelect?.()
              }}
            >
              {item.icon ? <span className="shrink-0" aria-hidden="true">{item.icon}</span> : null}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
